/**
 * Utility functions for extracting and handling API error messages
 */

interface ApiErrorResponse {
  data?: {
    Failed?: string;
    errors?: string[];
    error?: string;
    message?: string;
    response?: string;
  };
  message?: string;
  status?: number;
}

interface ApiResult {
  data?: {
    Failed?: string;
    errors?: string[];
    error?: string;
    message?: string;
    response?: string;
  };
  message?: string;
  status?: number;
}

/**
 * Extracts error message from API response or error object
 * Handles various error response formats:
 * - result.data.Failed (may contain JSON string with errors array)
 * - result.data.errors (array of error strings)
 * - result.data.error
 * - result.data.message
 * - result.data.response
 * - result.message
 * - error.response.data.* (same structure as result.data)
 * 
 * @param source - Either an API result object or an error object from catch block
 * @param defaultMessage - Default error message if no error can be extracted
 * @returns Extracted error message string
 */
export const extractErrorMessage = (
  source: ApiResult | ApiErrorResponse | any,
  defaultMessage: string = "An error occurred"
): string => {
  let errorMessage = defaultMessage;

  // Handle result object (from executePost/executeGet)
  if (source?.data) {
    // Check for Failed field (common in error responses)
    if (source.data.Failed) {
      errorMessage = source.data.Failed;
      // If Failed contains JSON string, try to parse it
      if (typeof errorMessage === 'string' && errorMessage.includes('errors')) {
        try {
          const parsed = JSON.parse(errorMessage);
          if (parsed.errors && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
            errorMessage = parsed.errors[0];
          }
        } catch (e) {
          // If parsing fails, use the original message
        }
      }
    }
    // Check for errors array
    else if (source.data.errors && Array.isArray(source.data.errors) && source.data.errors.length > 0) {
      errorMessage = source.data.errors[0];
    }
    // Check for error field
    else if (source.data.error) {
      errorMessage = source.data.error;
    }
    // Check for message field
    else if (source.data.message) {
      errorMessage = source.data.message;
    }
    // Check for response field
    else if (source.data.response) {
      errorMessage = source.data.response;
    }
  }

  // Handle error object from catch block (error.response.data)
  if (source?.response?.data) {
    const responseData = source.response.data;
    
    // Check for Failed field
    if (responseData.Failed) {
      errorMessage = responseData.Failed;
      // Try to parse if it's a JSON string
      if (typeof errorMessage === 'string' && errorMessage.includes('errors')) {
        try {
          const parsed = JSON.parse(errorMessage);
          if (parsed.errors && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
            errorMessage = parsed.errors[0];
          }
        } catch (e) {
          // If parsing fails, use the original message
        }
      }
    }
    // Check for errors array
    else if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
      errorMessage = responseData.errors[0];
    }
    // Check for error field
    else if (responseData.error) {
      errorMessage = responseData.error;
    }
    // Check for message field
    else if (responseData.message) {
      errorMessage = responseData.message;
    }
    // Check for response field
    else if (responseData.response) {
      errorMessage = responseData.response;
    }
  }

  // Also check source.message (set by useApi hook or error object)
  if (source?.message && (!errorMessage || errorMessage === defaultMessage || !errorMessage.includes(source.message))) {
    errorMessage = source.message;
  }

  return errorMessage;
};

export interface ValidationDetail {
  field: string;
  message: string;
}

const parseFailedPayload = (failed: unknown): any => {
  if (typeof failed !== "string") return undefined;
  try {
    return JSON.parse(failed);
  } catch (e) {
    return undefined;
  }
};

/**
 * Extracts field-level validation details from API responses shaped like
 * {errors: [...], details: [{field, message}, ...]} — including responses where
 * that JSON arrives stringified under a `Failed` key (APIM/container-app wrapper).
 *
 * @param source - Either an API result object or an error object from catch block
 * @returns Array of {field, message} validation details (empty when none found)
 */
export const extractValidationDetails = (
  source: ApiResult | ApiErrorResponse | any
): ValidationDetail[] => {
  const candidates = [source?.data, source?.response?.data];
  for (const data of candidates) {
    if (!data) continue;
    const payload = parseFailedPayload(data.Failed) ?? data;
    if (Array.isArray(payload?.details)) {
      return payload.details
        .filter((detail: any) => detail && (detail.field || detail.message))
        .map((detail: any) => ({
          field: String(detail.field ?? ""),
          message: String(detail.message ?? ""),
        }));
    }
  }
  return [];
};

/**
 * Error handler utility object
 */
export const ErrorHandler = {
  /**
   * Extracts error message from API response or error object
   * @param source - Either an API result object or an error object from catch block
   * @param defaultMessage - Default error message if no error can be extracted
   * @returns Extracted error message string
   */
  extractErrorMessage,
  /**
   * Extracts field-level validation details ({field, message}) from API responses
   * @param source - Either an API result object or an error object from catch block
   * @returns Array of validation details (empty when none found)
   */
  extractValidationDetails,
};

